"use client";

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { supabase } from '../lib/supabase';
import { getURL } from '../lib/url';
import { validateEmail, validatePassword } from '../lib/email-validation';

interface User {
    id: string; // Internal DB ID or Supabase ID? Let's treat this as the DB User object which has credits.
    email: string;
    credits: number;
    supabaseId: string;
    emailVerified: boolean;
    username?: string;
    isNewUser?: boolean; // Flag to indicate first-time login
}

interface AuthContextType {
    user: User | null; // This is our DB user concept
    isLoading: boolean;
    token: string | null;
    showWelcomePopup: boolean;
    dismissWelcomePopup: () => void;
    signInWithGoogle: () => Promise<void>;
    signUpWithEmail: (email: string, password: string, username: string) => Promise<{ success: boolean; error?: string }>;
    signInWithEmail: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
    resendVerificationEmail: () => Promise<{ success: boolean; error?: string }>;
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_AUTH = "/api/auth";

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showWelcomePopup, setShowWelcomePopup] = useState(false);

    useEffect(() => {
        // Listen for Supabase Auth State Changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
            if (session) {
                setToken(session.access_token);
                // Sync with backend to get credits
                await fetchUser(session.access_token);
            } else {
                setToken(null);
                setUser(null);
                setIsLoading(false);
            }
        });

        return () => subscription.unsubscribe();
    }, []);

    const fetchUser = async (authToken: string) => {
        try {
            // Get Supabase user to check email verification status and username
            const { data: { user: supabaseUser } } = await supabase.auth.getUser(authToken);
            
            // This endpoint validates the token (using internal verify we wrote) and returns the DB user (with credits)
            const response = await fetch(`${API_AUTH}/me`, {
                headers: {
                    'Authorization': `Bearer ${authToken}`
                }
            });
            if (response.ok) {
                const userData = await response.json();
                
                // Check if welcome popup has been dismissed for this user
                const welcomeDismissed = localStorage.getItem(`welcome-dismissed-${userData.id}`);
                
                // Show welcome popup if user is new and hasn't dismissed it
                if (userData.isNewUser && !welcomeDismissed) {
                    setShowWelcomePopup(true);
                }
                
                // Add email verification status and username from Supabase
                setUser({
                    ...userData,
                    emailVerified: !!supabaseUser?.email_confirmed_at,
                    username: supabaseUser?.user_metadata?.username || userData.email.split('@')[0]
                });
            } else {
                console.error("Backend validation failed for user");
            }
        } catch (error) {
            console.error("Failed to fetch user", error);
        } finally {
            setIsLoading(false);
        }
    };

    const dismissWelcomePopup = () => {
        if (user?.id) {
            localStorage.setItem(`welcome-dismissed-${user.id}`, 'true');
        }
        setShowWelcomePopup(false);
    };

    const signInWithGoogle = async () => {
        const { error } = await supabase.auth.signInWithOAuth({
            provider: 'google',
            options: {
                redirectTo: `${getURL()}/auth/callback`
            }
        });
        if (error) console.error("Google login error", error);
    };

    const signUpWithEmail = async (email: string, password: string, username: string) => {
        // Validate email
        const emailValidation = validateEmail(email);
        if (!emailValidation.valid) {
            return { success: false, error: emailValidation.error };
        }
        
        // Validate password
        const passwordValidation = validatePassword(password);
        if (!passwordValidation.valid) {
            return { success: false, error: passwordValidation.error };
        }

        // Validate username
        if (!username || username.trim().length < 2) {
            return { success: false, error: 'Username must be at least 2 characters' };
        }
        
        try {
            const { error } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    emailRedirectTo: `${getURL()}/auth/callback`,
                    data: {
                        username: username.trim()
                    }
                }
            });
            
            if (error) {
                // Check if error is due to existing user
                if (error.message.includes('already registered')) {
                    return { success: false, error: 'This email is already registered. Please sign in instead.' };
                }
                return { success: false, error: error.message };
            }
            
            return { success: true };
        } catch (err: any) {
            return { success: false, error: err.message || 'Sign up failed' };
        }
    };

    const signInWithEmail = async (email: string, password: string) => {
        try {
            const { error } = await supabase.auth.signInWithPassword({
                email,
                password
            });
            
            if (error) {
                return { success: false, error: error.message };
            }
            
            return { success: true };
        } catch (err: any) {
            return { success: false, error: err.message || 'Sign in failed' };
        }
    };

    const resetPassword = async (email: string) => {
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${getURL()}/auth/callback?type=recovery`
            });
            
            if (error) {
                return { success: false, error: error.message };
            }
            
            return { success: true };
        } catch (err: any) {
            return { success: false, error: err.message || 'Failed to send reset email' };
        }
    };

    const resendVerificationEmail = async () => {
        try {
            const { data: { user: currentUser } } = await supabase.auth.getUser();
            
            if (!currentUser?.email) {
                return { success: false, error: 'No user email found' };
            }
            
            const { error } = await supabase.auth.resend({
                type: 'signup',
                email: currentUser.email,
                options: {
                    emailRedirectTo: `${getURL()}/auth/callback`
                }
            });
            
            if (error) {
                return { success: false, error: error.message };
            }
            
            return { success: true };
        } catch (err: any) {
            return { success: false, error: err.message || 'Failed to resend email' };
        }
    };

    const router = useRouter();

    const logout = async () => {
        await supabase.auth.signOut();
        setToken(null);
        setUser(null);
        router.push('/');
    };

    return (
        <AuthContext.Provider value={{ 
            user, 
            isLoading, 
            token,
            showWelcomePopup,
            dismissWelcomePopup,
            signInWithGoogle, 
            signUpWithEmail,
            signInWithEmail,
            resetPassword,
            resendVerificationEmail,
            logout, 
            refreshUser: () => token ? fetchUser(token) : Promise.resolve() 
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
